import {useEffect} from "react";
import {useRef} from "react";
import {useDispatch, useSelector} from "react-redux";
import {difference, has} from "lodash";
import {v4 as uuidv4} from 'uuid';
import {
  setArticlesLoading,
  setArticlesReady,
  setArticlesUnavailable,
  setArticlesVisible
} from "../redux/applicationActions";
import {RootState} from "../redux/store";
import useCurrentRoute from "./useCurrentRoute";
import {INITIAL_ROUTE} from "./useRoutes";

const useArticlesLoaded = (): any => {
  const pathname = useCurrentRoute();
  const dispatch = useDispatch();

  const {
    articles,
    articles_offset,
    articles_visible,
    articles_ready,
    articles_loading,
    articles_failed,
    articles_unavailable,
    page_size,
  } = useSelector((state: RootState) => {
    const {config, page_size, routes} = state.application;
    const {base} = config;
    const route = has(routes, pathname) ? routes[pathname] : INITIAL_ROUTE;
    const {
      articles,
      articles_visible,
      articles_ready,
      articles_loading,
      articles_failed,
      articles_unavailable
    } = route;

    const articles_offset = (articles || []).length % page_size
      ? ((articles || []).length + page_size) - ((articles || []).length % page_size) // penultimate page
      : (articles || []).length ? ((articles || []).length) : 0

    return {
      articles,
      articles_offset,
      articles_visible: (articles_visible || []),
      articles_ready: (articles_ready || []),
      articles_loading: (articles_loading || []),
      articles_failed: (articles_failed || []),
      articles_unavailable,
      base,
      page_size,
    }
  });

  const current_route = useRef<string>(null);
  const current_visible_articles = useRef<number>(null);

  useEffect(() => {
    if (!pathname || current_route.current === pathname) return;

    if (current_route.current) {
      dispatch(setArticlesVisible(current_route.current, []));
    }
    current_route.current = pathname;
  }, [current_route, pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(()  => {
      if (!(articles || []).length) return;

      current_visible_articles.current = articles_offset - page_size;
      const start = articles_offset - (page_size * 2);
      const end = (start < 0 ? 0 : start) + page_size; // zero-indexed

      dispatch(setArticlesLoading(
        pathname,
        uuidv4(),
        articles_unavailable ? articles : articles.slice((articles_visible || []).length ? start : 0, end)
      ));
      if ((articles || []).length < page_size) dispatch(setArticlesUnavailable(pathname));
  }, [articles, articles_offset, pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      !articles_failed ||
      !articles_ready ||
      !Array.isArray(articles_visible)
    ) return;

    const results = [...articles_failed, ...articles_ready];

    if (!results.length) return;

    Object.entries(articles_loading).map(([id, articles]) => {
      if (!articles?.length) return;

      dispatch(setArticlesLoading(pathname, id, []));
      dispatch(setArticlesReady(pathname, difference(articles, results))); // should always be []
      dispatch(setArticlesVisible(pathname, [...articles_visible, ...articles]));
    })
  }, [articles_failed, articles_loading, articles_ready, articles_visible]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

export default useArticlesLoaded;
